import { BrowserModule, Title } from '@angular/platform-browser';
import { NgModule, CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA, APP_INITIALIZER } from '@angular/core';
import { RouterModule } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule, HttpClient, HTTP_INTERCEPTORS, HttpBackend, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceModule } from '@services/service.module';
import { MaterialModule } from './material.module';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { LangPaginatorIntl } from './lang-paginator-intl';
import { PagesModule } from '@pages/pages.module';
import { SharedModule } from '@shared/shared.module';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ToastrModule } from 'ngx-toastr';
import { TranslateModule, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { HttpErrorInterceptor } from '@services/interceptors/http-error.interceptor';
import { ToastrCustomActionComponent } from '@pages/index.pages';
import { LanguageService } from '@services/index.service';
import { firstValueFrom } from 'rxjs';
import { environment } from '@environments/environment';
// Guards
import { AuthGuard } from '@guards/auth.guard';
import { SpConfigGuard } from '@guards/sp-config.guard';
import { PasswordUpdateGuard } from '@guards/password-update.guard';
import { ModuleGuard } from '@guards/module.guard';
import { PermissionGuard } from '@guards/permission.guard';
import { DefaultRouteGuard } from '@guards/default-route.guard';
import { LoginByTokenGuard } from '@guards/login-by-token.guard';
import { ErrorPathGuard } from '@guards/error-path.guard';
// MSAL
import { MsalModule, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MSAL_GUARD_CONFIG, MsalInterceptor, MsalService, MsalBroadcastService, MsalGuard } from '@azure/msal-angular';
import { MsalRuntimeConfigService } from './msgraph/msal-runtime-config.service';
import { msalInstanceFactoryDynamic, msalInterceptorConfigFactoryDynamic, msalGuardConfigFactoryDynamic } from './msgraph/msal.graph.dynamic.config';

// ---------- i18n loader ----------
export function HttpLoaderFactory(http: HttpBackend) {
  const currentVersion = environment.VERSION;
  if (currentVersion) {
    return new TranslateHttpLoader(new HttpClient(http), './assets/i18n/', '.json?v=' + currentVersion);
  }
  return new TranslateHttpLoader(new HttpClient(http), './assets/i18n/', '.json?t=' + new Date().getTime());
}

export function appLangInitFactory(lang: LanguageService) {
  return async () => {
    await firstValueFrom(lang.configureApplicationLanguage());
  };
}

export function langInitFactory(lang: LanguageService) {
  return async () => {
    await firstValueFrom(lang.configureApplicationLanguage());
  };
}

@NgModule({
  schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
  declarations: [AppComponent],
  bootstrap: [AppComponent],

  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    MsalModule,
    ServiceModule,
    SharedModule,
    PagesModule,
    AppRoutingModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    MaterialModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpBackend],
      },
    }),
    ToastrModule.forRoot({
      toastComponent: ToastrCustomActionComponent,
      closeButton: true,
      timeOut: 10000,
      extendedTimeOut: 5000,
      disableTimeOut: false,
      easing: 'ease-in',
      easeTime: 500,
      enableHtml: true,
      progressBar: true,
      progressAnimation: 'increasing',
      positionClass: 'toast-top-right-custom',
      toastClass: 'ngx-toastr',
      titleClass: 'toast-title',
      messageClass: 'toast-message',
      iconClasses: {
        error: 'toast-error',
        info: 'toast-info',
        success: 'toast-success',
        warning: 'toast-warning',
      },
      tapToDismiss: false,
      onActivateTick: false,
      maxOpened: 4,
      autoDismiss: true,
      newestOnTop: true,
      preventDuplicates: false,
      resetTimeoutOnDuplicate: false,
    }),
  ],

  providers: [
    Title,
    AuthGuard,
    SpConfigGuard,
    PasswordUpdateGuard,
    ModuleGuard,
    PermissionGuard,
    DefaultRouteGuard,
    LoginByTokenGuard,
    ErrorPathGuard,
    {
      provide: MatPaginatorIntl,
      deps: [TranslateService],
      useFactory: (translateService: TranslateService) =>
        new LangPaginatorIntl(translateService).getLangPaginatorIntl(),
    },
    {
      provide: APP_INITIALIZER,
      useFactory: appLangInitFactory,
      deps: [LanguageService],
      multi: true,
    },
    { provide: MSAL_INSTANCE, useFactory: msalInstanceFactoryDynamic, deps: [MsalRuntimeConfigService] },
    { provide: MSAL_INTERCEPTOR_CONFIG, useFactory: msalInterceptorConfigFactoryDynamic, deps: [MsalRuntimeConfigService] },
    { provide: MSAL_GUARD_CONFIG, useFactory: msalGuardConfigFactoryDynamic, deps: [MsalRuntimeConfigService] },
    MsalService,
    MsalBroadcastService,
    MsalGuard,
    { provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true },
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
